import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlertGroup } from '@d-orca/shared/alert-kinds';
import { ActivityIcon, ChartColumnIcon, IssuesIcon, ShieldIcon } from '@utils/icons';
import styles from './AlertGroupCard.module.css';

const GROUP_ICONS: Record<AlertGroup, ComponentType<{ size?: number }>> = {
  safety: ShieldIcon,
  reports: ChartColumnIcon,
  activities: ActivityIcon,
  issues: IssuesIcon,
};

export interface IAlertGroupCardProps {
  group: AlertGroup;
  /** Header action, right-aligned (the Hub's "+ Create" on the safety card). */
  action?: ReactNode;
  /** The group's rows; the card draws the dividers between them. */
  children: ReactNode;
}

/** One group of alert settings: icon and title, an optional action, then its rows. */
export function AlertGroupCard({ group, action, children }: IAlertGroupCardProps) {
  const { t } = useTranslation();
  const Icon = GROUP_ICONS[group];
  const titleId = `alert-group-${group}`;
  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <header className={styles.header}>
        <span className={styles.icon} aria-hidden="true">
          <Icon size={18} />
        </span>
        <h2 className={styles.title} id={titleId}>
          {t(`alertSettings.groups.${group}`)}
        </h2>
        {action && <div className={styles.action}>{action}</div>}
      </header>
      <div className={styles.rows}>{children}</div>
    </section>
  );
}
